import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import api, { API, authToken } from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { formatDate, currency } from "../../lib/format";

export default function PortalBilling() {
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get("/invoices/mine").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);

  const totalDue = invoices
    .filter((i) => ["sent", "overdue", "due"].includes(i.display_status))
    .reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-8" data-testid="portal-billing">
      <div>
        <p className="overline mb-2">Your account</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">My Billing</h1>
        <p className="font-sans text-sm text-stone mt-2">
          Independent classes are billed by the studio. Pay by Venmo, Zelle, or PayPal — include your invoice number.
        </p>
      </div>

      <div className="paper-card p-6 flex items-center justify-between">
        <p className="font-sans text-sm text-stone uppercase tracking-wider">Outstanding</p>
        <p className="font-mono text-3xl text-garnet">{currency(totalDue)}</p>
      </div>

      <div className="space-y-4">
        {invoices.map((inv) => (
          <div key={inv.id} className="paper-card p-6" data-testid={`invoice-${inv.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-ink">{inv.number}</span>
                  <StatusMark status={inv.display_status} />
                </div>
                <p className="font-serif text-2xl text-ink mt-1">{inv.class_title}</p>
                <p className="font-sans text-xs text-stone mt-1">
                  Issued {formatDate(inv.issued_date)} · Due {formatDate(inv.due_date)}
                </p>
              </div>
              <div className="flex items-center gap-5">
                <span className="font-mono text-2xl text-ink">{currency(inv.amount)}</span>
                <button
                  onClick={() => window.open(`${API}/invoices/${inv.id}/pdf?auth=${authToken()}`, "_blank")}
                  className="btn-outline text-sm"
                  data-testid={`invoice-pdf-${inv.id}`}
                >
                  <Download size={15} /> Invoice
                </button>
              </div>
            </div>

            {["sent", "overdue", "due", "draft"].includes(inv.display_status) && inv.pay_handles_snapshot && (
              <div className="mt-5 pt-4 border-t border-hairline bg-paper">
                <p className="font-sans text-xs uppercase tracking-wider text-stone mb-2">How to pay</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 font-mono text-sm text-ink">
                  {Object.entries(inv.pay_handles_snapshot).map(([k, v]) => (
                    <div key={k}>
                      <span className="text-stone">{k}:</span> {v}
                    </div>
                  ))}
                </div>
                <p className="font-sans text-xs text-stone mt-2">
                  Include the invoice number ({inv.number}) in your note.
                </p>
              </div>
            )}
          </div>
        ))}
        {invoices.length === 0 && (
          <p className="font-sans text-sm text-stone">No invoices on your account.</p>
        )}
      </div>
    </div>
  );
}
